/**
 * Authentication Service - Professional Session Management
 * Handles authentication state, token management, and session persistence
 */

const AUTH_STORAGE_KEYS = {
  TOKEN: 'token',
  USER: 'user',
  SESSION_ID: 'session_id',
  LAST_ACTIVITY: 'last_activity'
};

class AuthService {
  constructor() {
    this.listeners = [];
    this.sessionCheckInterval = null;
    this.initializeSession();
  }

  /**
   * Initialize session tracking
   */
  initializeSession() {
    // Generate session ID if doesn't exist
    if (!localStorage.getItem(AUTH_STORAGE_KEYS.SESSION_ID)) {
      localStorage.setItem(AUTH_STORAGE_KEYS.SESSION_ID, this.generateSessionId());
    }
    
    // Update last activity
    this.updateActivity();
    
    // Set up activity tracking
    this.setupActivityTracking();
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Track user activity to keep session alive
   */
  setupActivityTracking() {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, () => this.updateActivity(), { passive: true });
    });
  }

  /**
   * Update last activity timestamp
   */
  updateActivity() {
    try {
      localStorage.setItem(AUTH_STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
    } catch (error) {
      console.warn('Failed to update activity timestamp:', error);
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    try {
      const token = this.getToken();
      const user = this.getUser();
      
      // Must have both token and user
      if (!token || !user) {
        return false;
      }

      // Token exists and is valid format
      if (typeof token === 'string' && token.length > 20) {
        this.updateActivity();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  }

  /**
   * Check if user is admin
   * @returns {boolean}
   */
  isAdmin() {
    try {
      const user = this.getUser();
      return user && user.is_admin === true;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  /**
   * Get authentication token
   * @returns {string|null}
   */
  getToken() {
    try {
      return localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  /**
   * Get user data
   * @returns {object|null}
   */
  getUser() {
    try {
      const userStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error getting user:', error);
      return null;
    }
  }

  /**
   * Set authentication data (login)
   * @param {string} token - JWT token
   * @param {object} user - User data
   */
  setAuth(token, user) {
    try {
      localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user));
      this.updateActivity();
      this.notifyListeners({ type: 'login', user });
      console.log('✓ Authentication set successfully');
    } catch (error) {
      console.error('Error setting authentication:', error);
      throw new Error('Failed to save authentication data');
    }
  }

  /**
   * Clear authentication data (logout)
   * @param {boolean} notify - Whether to notify listeners
   */
  clearAuth(notify = true) {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
      localStorage.removeItem(AUTH_STORAGE_KEYS.USER);
      
      if (notify) {
        this.notifyListeners({ type: 'logout' });
      }
      
      console.log('✓ Authentication cleared');
    } catch (error) {
      console.error('Error clearing authentication:', error);
    }
  }

  /**
   * Get authorization header for API requests
   * @returns {object}
   */
  getAuthHeader() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Subscribe to auth state changes
   * @param {function} listener - Callback function
   * @returns {function} Unsubscribe function
   */
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of auth state change
   * @param {object} event - Event data
   */
  notifyListeners(event) {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in auth listener:', error);
      }
    });
  }

  /**
   * Validate token format (basic check)
   * @param {string} token - JWT token
   * @returns {boolean}
   */
  validateTokenFormat(token) {
    if (!token || typeof token !== 'string') return false;
    
    // JWT should have 3 parts separated by dots
    const parts = token.split('.');
    return parts.length === 3;
  }

  /**
   * Handle API authentication error
   * @param {object} error - Axios error object
   * @returns {boolean} True if handled (logout needed)
   */
  handleAuthError(error) {
    if (error.response) {
      const status = error.response.status;
      
      // Only logout on explicit auth failures
      if (status === 401 && error.response.data?.detail?.includes('Invalid token')) {
        console.warn('Invalid token detected, clearing auth');
        this.clearAuth();
        return true;
      }
    }
    
    return false;
  }

  /**
   * Get session info for debugging
   * @returns {object}
   */
  getSessionInfo() {
    return {
      isAuthenticated: this.isAuthenticated(),
      isAdmin: this.isAdmin(),
      hasToken: !!this.getToken(),
      hasUser: !!this.getUser(),
      sessionId: localStorage.getItem(AUTH_STORAGE_KEYS.SESSION_ID),
      lastActivity: localStorage.getItem(AUTH_STORAGE_KEYS.LAST_ACTIVITY)
    };
  }
}

// Export singleton instance
const authService = new AuthService();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  window.authService = authService;
}

export default authService;
